import React, { useState, useRef, useEffect } from 'react';

const SignatureEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [showHtmlMode, setShowHtmlMode] = useState(false);
  const [rawHtml, setRawHtml] = useState(value || '');

  useEffect(() => {
    if (editorRef.current && !showHtmlMode) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, showHtmlMode]);

  const handleEditorChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setRawHtml(html);
      onChange(html);
    }
  };

  const handleRawHtmlChange = (e) => {
    const html = e.target.value;
    setRawHtml(html);
    onChange(html);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleEditorChange();
  };

  const insertLink = () => {
    const url = prompt('Enter URL (include https://):');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const handlePaste = (e) => {
    // Allow rich paste from Gmail/Outlook
    // The browser will handle HTML paste automatically with contenteditable
    setTimeout(handleEditorChange, 0);
  };

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-zinc-800 rounded-lg border border-zinc-700">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-2 hover:bg-zinc-700 rounded text-zinc-300 font-bold"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-2 hover:bg-zinc-700 rounded text-zinc-300 italic"
          title="Italic"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-2 hover:bg-zinc-700 rounded text-zinc-300 underline"
          title="Underline"
        >
          U
        </button>
        
        <div className="w-px h-6 bg-zinc-600 mx-1" />
        
        <button
          type="button"
          onClick={insertLink}
          className="p-2 hover:bg-zinc-700 rounded text-zinc-300"
          title="Insert Link"
        >
          🔗
        </button>
        <button
          type="button"
          onClick={insertImage}
          className="p-2 hover:bg-zinc-700 rounded text-zinc-300"
          title="Insert Image"
        >
          🖼️
        </button>
        
        <div className="w-px h-6 bg-zinc-600 mx-1" />
        
        <select
          onChange={(e) => {
            if (e.target.value) {
              execCommand('fontSize', e.target.value);
            }
          }}
          className="bg-zinc-700 text-zinc-300 rounded px-2 py-1 text-sm"
          defaultValue=""
        >
          <option value="" disabled>Size</option>
          <option value="1">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
        </select>
        
        <input
          type="color"
          onChange={(e) => execCommand('foreColor', e.target.value)}
          className="w-8 h-8 rounded cursor-pointer bg-transparent"
          title="Text Color"
          defaultValue="#ffffff"
        />
        
        <div className="flex-1" />
        
        <button
          type="button"
          onClick={() => setShowHtmlMode(!showHtmlMode)}
          className={`px-3 py-1 rounded text-xs font-medium ${
            showHtmlMode 
              ? 'bg-nextide-turquoise text-black' 
              : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
          }`}
        >
          {showHtmlMode ? 'Visual' : 'HTML'}
        </button>
      </div>

      {/* Editor */}
      {showHtmlMode ? (
        <textarea
          value={rawHtml}
          onChange={handleRawHtmlChange}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white font-mono text-sm focus:outline-none focus:border-nextide-turquoise min-h-[200px] resize-y"
          placeholder="Paste your HTML signature here..."
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleEditorChange}
          onPaste={handlePaste}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-nextide-turquoise min-h-[200px] overflow-auto"
          style={{ 
            lineHeight: '1.5',
            wordBreak: 'break-word'
          }}
          data-placeholder="Paste your signature from Gmail, or build one here..."
        />
      )}

      {/* Preview */}
      <div className="mt-4">
        <div className="text-xs text-zinc-500 mb-2">Preview:</div>
        <div 
          className="bg-white rounded-lg p-4 text-black text-sm"
          style={{ minHeight: '80px' }}
          dangerouslySetInnerHTML={{ __html: rawHtml || '<span style="color: #999;">Your signature will appear here...</span>' }}
        />
      </div>

      {/* Help Text */}
      <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-zinc-400 space-y-2">
        <div className="font-medium text-zinc-300">Tips:</div>
        <ul className="space-y-1 list-disc list-inside">
          <li><strong>From Gmail:</strong> Open a sent email → Select your signature → Copy → Paste here</li>
          <li><strong>Images:</strong> Use the image button and paste an image URL (hosted online)</li>
          <li><strong>Links:</strong> Select text, then click the link button to add a URL</li>
          <li><strong>HTML mode:</strong> For advanced users, paste raw HTML directly</li>
        </ul>
      </div>
    </div>
  );
};

export default SignatureEditor;
